// RITA-1525 — Eine A4-Seite mit Kopfzeile (Logo), Textbereich und Fußzeile (Seitenzahl).
// Alle Maße stehen in Millimetern; Chromium rechnet sie auf dem Bildschirm und im Druck gleich um.
//
// Bearbeitung (Teilpaket 2): Die Seite liegt in einem gemeinsamen contentEditable-Host; die
// Bereiche, die gerade nicht bearbeitet werden, sind contenteditable="false"-Inseln. Im Bereich
// `text` sind alle Textbereiche editierbar (Auswahl über Seitengrenzen hinweg ist eine Range);
// bei `kopf`/`fuss` nur die Kopf- bzw. Fußzeile der aktiven Seite – die Änderung gilt für alle Seiten.
import React, { useMemo } from 'react';
import { abstandsstil } from '../model/abstaende';
import { Wertemodus } from '../model/platzhalterwerte';
import { Bereich } from '../model/positionen';
import { Block, HorizontalePosition, Vertragsdokument } from '../model/types';
import { Seitengeometrie } from '../layout/seitengeometrie';
import { FUSSZEILE_GROESSE_PT } from '../model/schriftgroessen';
import BlockAnsicht from './BlockAnsicht';
import { useVertragsarten, vertragsartFinden } from './VertragsartenKontext';

export interface Bearbeitungsstand {
  bereich: Bereich;
  aktiveSeite: number;
  /** Erzwingt den Neuaufbau aller Seiten (nach einer Komposition, die das DOM selbst verändert hat). */
  renderVersion?: number;
}

interface Props {
  nummer: number;
  gesamt: number;
  dokument: Vertragsdokument;
  blockIds: string[];
  ueberlauf: boolean;
  modus: Wertemodus;
  geometrie: Seitengeometrie;
  bearbeitung?: Bearbeitungsstand;
  gesperrt?: boolean;
}

const AUSRICHTUNG: Record<HorizontalePosition, 'flex-start' | 'center' | 'flex-end'> = {
  links: 'flex-start',
  mitte: 'center',
  rechts: 'flex-end',
};

const TEXTAUSRICHTUNG: Record<HorizontalePosition, 'left' | 'center' | 'right'> = {
  links: 'left',
  mitte: 'center',
  rechts: 'right',
};

/** Spalten der Fußzeile: der Fußtext bekommt die volle Restbreite, die Seitenzahl nur ihren Platz. */
const FUSS_RASTER: Record<HorizontalePosition, { spalten: string; textSpalte: number; zahlSpalte: number }> = {
  links: { spalten: 'auto 1fr', textSpalte: 2, zahlSpalte: 1 },
  mitte: { spalten: '1fr auto 1fr', textSpalte: 1, zahlSpalte: 2 },
  rechts: { spalten: '1fr auto', textSpalte: 1, zahlSpalte: 2 },
};

function VertragSeite({
  nummer,
  gesamt,
  dokument,
  blockIds,
  ueberlauf,
  modus,
  geometrie,
  bearbeitung,
  gesperrt,
}: Props) {
  const { raender } = dokument.seite;
  const { logo } = dokument.kopf;
  const { seitenzahl } = dokument.fuss;
  // Das Logo kommt von der gewählten Vertragsart (RITA-1526); ohne Vertragsart oder ohne Logo entfällt die Zeile.
  const vertragsart = vertragsartFinden(useVertragsarten(), logo.vertragsartId);
  const logoBild = vertragsart?.logoDataUrl ? { quelle: vertragsart.logoDataUrl, titel: vertragsart.titel } : null;
  const bloecke = useMemo(() => {
    const nachId = new Map<string, Block>(dokument.text.map((block) => [block.id, block]));
    return blockIds.map((id) => nachId.get(id)).filter((block): block is Block => block !== undefined);
  }, [blockIds, dokument.text]);

  const fussRaster = seitenzahl.anzeigen
    ? FUSS_RASTER[seitenzahl.position]
    : { spalten: '1fr', textSpalte: 1, zahlSpalte: 1 };

  // undefined = editierbar (erbt vom Host), false = Insel. Ohne Bearbeitung keine Attribute.
  const insel = (editierbar: boolean): false | undefined => {
    if (!bearbeitung) return undefined;
    return editierbar ? undefined : false;
  };
  const aktiv = (bereich: Bereich): boolean =>
    !!bearbeitung && bearbeitung.bereich === bereich && (bereich === 'text' || bearbeitung.aktiveSeite === nummer);
  const bearbeitbar = !!bearbeitung;

  return (
    <section
      className={ueberlauf ? 'vertrag-seite vertrag-ueberlauf' : 'vertrag-seite'}
      data-testid="vertrag-seite"
      data-seite={nummer}
      data-aktiv={bearbeitung && bearbeitung.aktiveSeite === nummer ? 'true' : undefined}
      aria-label={`Seite ${nummer} von ${gesamt}`}
      style={{ fontSize: `${dokument.schrift.groessePt}pt`, ...abstandsstil(dokument.schrift) }}
    >
      <header
        className={aktiv('kopf') ? 'vertrag-kopf vertrag-bereich-aktiv' : 'vertrag-kopf'}
        data-bereich="kopf"
        contentEditable={insel(aktiv('kopf'))}
        style={{
          height: `${raender.obenMm + geometrie.kopfHoeheMm}mm`,
          padding: `${raender.obenMm}mm ${raender.rechtsMm}mm 0 ${raender.linksMm}mm`,
        }}
      >
        {logoBild && (
          <div
            className="vertrag-kopf-zeile"
            style={{ justifyContent: AUSRICHTUNG[logo.position] }}
            contentEditable={insel(false)}
          >
            <img
              className="vertrag-logo"
              src={logoBild.quelle}
              alt={`Logo ${logoBild.titel}`}
              // Höhe fest, Breite höchstens die Zeile: ein sehr breites Logo wird verkleinert statt abgeschnitten.
              style={{
                height: `${logo.hoeheMm}mm`,
                maxWidth: '100%',
                objectFit: 'contain',
                objectPosition: TEXTAUSRICHTUNG[logo.position],
              }}
            />
          </div>
        )}
        {dokument.kopf.inhalt.map((block) => (
          <BlockAnsicht key={block.id} block={block} modus={modus} />
        ))}
      </header>
      <div
        className={aktiv('text') ? 'vertrag-inhalt vertrag-bereich-aktiv' : 'vertrag-inhalt'}
        data-testid="vertrag-inhalt"
        data-bereich="text"
        contentEditable={insel(aktiv('text'))}
        style={{
          height: `${geometrie.inhaltHoeheMm}mm`,
          padding: `0 ${raender.rechtsMm}mm 0 ${raender.linksMm}mm`,
        }}
      >
        {bloecke.map((block) => (
          <BlockAnsicht key={block.id} block={block} modus={modus} bearbeitbar={bearbeitbar} gesperrt={gesperrt} />
        ))}
      </div>
      <footer
        className="vertrag-fuss"
        data-bereich="fuss"
        contentEditable={insel(aktiv('fuss'))}
        style={{
          height: `${geometrie.fussHoeheMm + raender.untenMm}mm`,
          padding: `0 ${raender.rechtsMm}mm ${raender.untenMm}mm ${raender.linksMm}mm`,
          // Feste Standardgröße der Fußzeile samt Seitenzahl; markierter Fußtext kann eine eigene Größe tragen (RITA-1534).
          fontSize: `${FUSSZEILE_GROESSE_PT}pt`,
          gridTemplateColumns: fussRaster.spalten,
        }}
      >
        <div
          className={aktiv('fuss') ? 'vertrag-fuss-inhalt vertrag-bereich-aktiv' : 'vertrag-fuss-inhalt'}
          style={{ gridColumn: fussRaster.textSpalte }}
        >
          {dokument.fuss.inhalt.map((block) => (
            <BlockAnsicht key={block.id} block={block} modus={modus} />
          ))}
        </div>
        {seitenzahl.anzeigen && (
          <span
            className="vertrag-seitenzahl"
            data-testid="vertrag-seitenzahl"
            contentEditable={insel(false)}
            style={{ gridColumn: fussRaster.zahlSpalte, textAlign: TEXTAUSRICHTUNG[seitenzahl.position] }}
          >
            {`Seite ${nummer} von ${gesamt}`}
          </span>
        )}
      </footer>
    </section>
  );
}

export default VertragSeite;
